// Module: bootstrap-record
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §2
//
// Addressing, schema and trust for the bootstrap record. Pure: no ceremony, no network, no DOM.
// This is the module that resolves v1.0's fatal circularity, so its correctness is what makes the
// system startable at all.

import type { KdfParams } from "../key-hierarchy_2026-08-27/key-hierarchy.js" // type-only

export type { KdfParams }

export type SlotType = "roaming" | "platform" | "recovery"

export type Slot = {
  id: string
  type: SlotType
  label: string
  credentialId?: Uint8Array
  /**
   * The VaultID this authenticator derives from its own locator PRF, and therefore the address of
   * its copy of the record (spec §2.4).
   *
   * It must be stored, because it can only be computed while that authenticator is physically
   * present. Without it, `session` could never fan a record update out to a backup key that is in
   * a drawer — the copy would silently go stale and the backup would open an old vault.
   *
   * Absent for recovery slots: no authenticator, no locator. The card carries the primary VaultID.
   */
  locatorVaultId?: string
  hkdfSalt: Uint8Array
  wrappedDek: Uint8Array
  createdAt: string
}

export type BootstrapRecord = {
  recordVersion: 2
  vaultId: string
  objectId: string
  revision: bigint
  kdf: KdfParams
  slots: Slot[]
}

export const RECORD_VERSION = 2
export const MAX_RECORD_BYTES = 64 * 1024
export const HKDF_SALT_BYTES = 16
export const KDF_SALT_BYTES = 16
export const WRAPPED_DEK_BYTES = 40

// The PRF input is no longer a pair of fixed constants: it is derived from the vault's name, so
// one credential can address many vaults. See `prfInputFor` and `normalizeVaultName` below.

/**
 * The client's own KDF floor, duplicated from `key-hierarchy` on purpose rather than imported.
 * Both modules enforce it independently: this one screens untrusted input at the trust boundary,
 * that one screens whatever reaches derivation. A publicly writable record makes two checks the
 * right number, and keeping the constant local also keeps this module free of runtime imports.
 */
const MIN_ITERATIONS = 600_000
const ALLOWED_ALGS = new Set(["PBKDF2-HMAC-SHA256"])

export type BootstrapErrorCode =
  | "RecordTooLarge"
  | "MalformedRecord"
  | "UnsupportedRecordVersion"
  | "WeakKdfParams"
  | "InvalidSlot"
  | "SlotNotFound"
  | "DuplicateSlot"

export class BootstrapError extends Error {
  readonly code: BootstrapErrorCode
  constructor(code: BootstrapErrorCode, message?: string) {
    super(message ?? code)
    this.name = "BootstrapError"
    this.code = code
  }
}

const enc = new TextEncoder()
const dec = new TextDecoder()
const src = (u: Uint8Array): BufferSource => u as unknown as BufferSource

// The explicit annotation is required: without it TypeScript will not treat a bare `fail(...)`
// statement as never-returning, and none of the type narrowing below works.
const fail: (code: BootstrapErrorCode, message?: string) => never = (code, message) => {
  throw new BootstrapError(code, message)
}

// --- encoding helpers --------------------------------------------------------------------

const HEX = "0123456789abcdef"

function toHex(bytes: Uint8Array): string {
  let out = ""
  for (const b of bytes) out += HEX[b >> 4]! + HEX[b & 0x0f]!
  return out
}

function b64u(bytes: Uint8Array): string {
  let binary = ""
  for (const b of bytes) binary += String.fromCharCode(b)
  return btoa(binary).replace(/\+/gu, "-").replace(/\//gu, "_").replace(/=+$/u, "")
}

function unb64u(text: string, field: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]*$/u.test(text)) fail("MalformedRecord", `${field} is not base64url`)
  const padded = text.replace(/-/gu, "+").replace(/_/gu, "/")
  try {
    const binary = atob(padded)
    const out = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i)
    return out
  } catch {
    return fail("MalformedRecord", `${field} is not decodable`)
  }
}

const isHex64 = (value: unknown): value is string =>
  typeof value === "string" && /^[0-9a-f]{64}$/u.test(value)

// --- addressing --------------------------------------------------------------------------

/** All the record addresses this vault is mirrored to (spec §2.4). */
export function locatorAddresses(record: BootstrapRecord): string[] {
  const seen = new Set<string>([record.vaultId])
  for (const slot of record.slots) if (slot.locatorVaultId) seen.add(slot.locatorVaultId)
  return [...seen]
}

/**
 * Vault names are typed by a person, so two strings a person would call the same name must derive
 * the same vault. NFKC folds compatibility forms, case is dropped, and whitespace, `-` and `_` are
 * removed entirely: `My Old Crypto Vault`, `my-old-crypto-vault` and `myoldcryptovault` are one
 * vault. Folding the separators follows the same reasoning as folding spaces — they are invisible
 * distinctions to someone naming a thing.
 *
 * Deliberately total: it never throws, not even for a name that normalizes to "". A name is a
 * selector, not a factor, and spec §0 puts a sentence where a block would do — so screening a
 * useless name belongs in `app`, where it can be advice, not here, where it could only be an
 * exception.
 */
export function normalizeVaultName(name: string): string {
  return name.normalize("NFKC").toLowerCase().replace(/[\s_-]+/gu, "")
}

/**
 * The one PRF input for a named vault: SHA-256("vault-prf-v2" ‖ normalizeVaultName(name)).
 *
 * One input, not the two fixed constants this replaced. `session` splits the resulting 32 bytes
 * into the locator and the hardware secret with HKDF (spec §2.2, §3.3), which is the same
 * purpose-separation every other subkey already uses (§3.5). Needing only `eval.first` is what
 * keeps this working on a platform that forwards one PRF salt but not two — requiring both was a
 * stronger assumption about the platform than the design ever needed to make.
 *
 * A mistyped name addresses a vault that does not exist, which presents as "no vault here". That
 * is why `app` must never answer a miss by offering to create one.
 */
export async function prfInputFor(name: string): Promise<Uint8Array> {
  const prefix = enc.encode("vault-prf-v2")
  const label = enc.encode(normalizeVaultName(name))
  const input = new Uint8Array(prefix.length + label.length)
  input.set(prefix, 0)
  input.set(label, prefix.length)
  return new Uint8Array(await crypto.subtle.digest("SHA-256", src(input)))
}

/** VaultID = SHA-256("vault-id-v1" ‖ locatorSecret), hex. An identifier, never key material. */
export async function deriveVaultId(locatorSecret: Uint8Array): Promise<string> {
  const prefix = enc.encode("vault-id-v1")
  const input = new Uint8Array(prefix.length + locatorSecret.length)
  input.set(prefix, 0)
  input.set(locatorSecret, prefix.length)
  return toHex(new Uint8Array(await crypto.subtle.digest("SHA-256", src(input))))
}

// --- validation --------------------------------------------------------------------------

function checkKdf(kdf: KdfParams): void {
  if (!ALLOWED_ALGS.has(kdf.alg)) fail("WeakKdfParams", `unsupported kdf algorithm: ${kdf.alg}`)
  if (!Number.isInteger(kdf.iterations) || kdf.iterations < MIN_ITERATIONS) {
    fail("WeakKdfParams", `iterations must be at least ${MIN_ITERATIONS}, got ${kdf.iterations}`)
  }
  if (kdf.salt.length !== KDF_SALT_BYTES) fail("WeakKdfParams", "kdf.salt must be 16 bytes")
}

function checkSlot(slot: Slot): void {
  if (typeof slot.id !== "string" || slot.id.length === 0) fail("InvalidSlot", "slot id is empty")
  if (slot.type !== "roaming" && slot.type !== "platform" && slot.type !== "recovery") {
    fail("InvalidSlot", `unknown slot type: ${String(slot.type)}`)
  }
  if (slot.hkdfSalt.length !== HKDF_SALT_BYTES) {
    fail("InvalidSlot", `slot ${slot.id}: hkdfSalt must be ${HKDF_SALT_BYTES} bytes`)
  }
  if (slot.wrappedDek.length !== WRAPPED_DEK_BYTES) {
    fail("InvalidSlot", `slot ${slot.id}: wrappedDek must be ${WRAPPED_DEK_BYTES} bytes`)
  }

  // The slot type determines which second factor exists. A record that disagrees is malformed,
  // not merely odd: it would send the client looking for a ceremony that cannot happen.
  const authenticatorBacked = slot.type === "roaming" || slot.type === "platform"
  if (authenticatorBacked) {
    if (!slot.credentialId || slot.credentialId.length === 0) {
      fail("InvalidSlot", `slot ${slot.id}: ${slot.type} slot needs a credentialId`)
    }
    if (!slot.locatorVaultId || !isHex64(slot.locatorVaultId)) {
      fail("InvalidSlot", `slot ${slot.id}: ${slot.type} slot needs a locatorVaultId`)
    }
  } else {
    if (slot.credentialId) fail("InvalidSlot", `slot ${slot.id}: recovery slot must not have a credentialId`)
    if (slot.locatorVaultId) {
      fail("InvalidSlot", `slot ${slot.id}: recovery slot must not have a locatorVaultId`)
    }
  }
}

function checkRecord(record: BootstrapRecord): void {
  if (record.recordVersion !== RECORD_VERSION) {
    fail("UnsupportedRecordVersion", `recordVersion ${String(record.recordVersion)}`)
  }
  if (!isHex64(record.vaultId)) fail("MalformedRecord", "vaultId must be 64 lowercase hex chars")
  if (!isHex64(record.objectId)) fail("MalformedRecord", "objectId must be 64 lowercase hex chars")
  if (typeof record.revision !== "bigint" || record.revision < 0n) {
    fail("MalformedRecord", "revision must be a non-negative bigint")
  }
  checkKdf(record.kdf)

  // A record with no slots can never be opened, so producing one is a bug rather than a state.
  if (record.slots.length === 0) fail("InvalidSlot", "a record must have at least one slot")

  const seen = new Set<string>()
  for (const slot of record.slots) {
    checkSlot(slot)
    if (seen.has(slot.id)) fail("DuplicateSlot", `duplicate slot id: ${slot.id}`)
    seen.add(slot.id)
  }
}

// --- construction ------------------------------------------------------------------------

/**
 * Enrolment builds a record that has never been serialised, and it must pass exactly the same
 * validation as one that arrived over the network. One validation path, two entry points.
 */
export function create(opts: {
  vaultId: string
  objectId: string
  kdf: KdfParams
  slots: Slot[]
}): BootstrapRecord {
  const record: BootstrapRecord = {
    recordVersion: RECORD_VERSION,
    vaultId: opts.vaultId,
    objectId: opts.objectId,
    revision: 0n,
    kdf: opts.kdf,
    slots: opts.slots,
  }
  checkRecord(record)
  return record
}

// --- canonical form ----------------------------------------------------------------------

function slotToJson(slot: Slot): Record<string, unknown> {
  const json: Record<string, unknown> = {
    createdAt: slot.createdAt,
    hkdfSalt: b64u(slot.hkdfSalt),
    id: slot.id,
    label: slot.label,
    type: slot.type,
    wrappedDek: b64u(slot.wrappedDek),
  }
  if (slot.credentialId) json["credentialId"] = b64u(slot.credentialId)
  if (slot.locatorVaultId) json["locatorVaultId"] = slot.locatorVaultId
  return json
}

function stringifyCanonical(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value)
  if (Array.isArray(value)) return `[${value.map(stringifyCanonical).join(",")}]`
  const entries = Object.entries(value as Record<string, unknown>).sort(([a], [b]) =>
    a < b ? -1 : a > b ? 1 : 0,
  )
  return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stringifyCanonical(v)}`).join(",")}}`
}

function recordToJson(record: BootstrapRecord): Record<string, unknown> {
  return {
    kdf: {
      alg: record.kdf.alg,
      iterations: record.kdf.iterations,
      salt: b64u(record.kdf.salt),
    },
    objectId: record.objectId,
    recordVersion: record.recordVersion,
    // A JSON string, not a number: the container header stores a uint64 (spec §4.1) and JSON
    // numbers lose precision above 2^53. Encoding one value two ways is how they diverge.
    revision: record.revision.toString(10),
    slots: [...record.slots]
      .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
      .map(slotToJson),
    vaultId: record.vaultId,
  }
}

/** Exactly what the MAC covers. */
export function canonicalBytes(record: BootstrapRecord): Uint8Array {
  return enc.encode(stringifyCanonical(recordToJson(record)))
}

export function serialize(record: BootstrapRecord, mac: Uint8Array): Uint8Array {
  checkRecord(record)
  return enc.encode(stringifyCanonical({ ...recordToJson(record), mac: b64u(mac) }))
}

/** Returns the MAC alongside the record, so "the MAC covers everything except itself" is
 * structural rather than a convention someone has to remember. */
export function parse(bytes: Uint8Array): { record: BootstrapRecord; mac: Uint8Array } {
  if (bytes.length > MAX_RECORD_BYTES) {
    fail("RecordTooLarge", `${bytes.length} bytes exceeds ${MAX_RECORD_BYTES}`)
  }

  let raw: unknown
  try {
    raw = JSON.parse(dec.decode(bytes))
  } catch (cause) {
    return fail("MalformedRecord", `not valid JSON: ${String(cause)}`)
  }
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return fail("MalformedRecord", "record is not an object")
  }
  const doc = raw as Record<string, unknown>

  if (typeof doc["mac"] !== "string") fail("MalformedRecord", "missing mac")
  if (doc["recordVersion"] !== RECORD_VERSION) {
    fail("UnsupportedRecordVersion", `recordVersion ${String(doc["recordVersion"])}`)
  }
  if (typeof doc["revision"] !== "string" || !/^\d+$/u.test(doc["revision"])) {
    fail("MalformedRecord", "revision must be a decimal string")
  }

  const kdfRaw = doc["kdf"]
  if (typeof kdfRaw !== "object" || kdfRaw === null) fail("MalformedRecord", "missing kdf")
  const k = kdfRaw as Record<string, unknown>
  if (typeof k["alg"] !== "string" || typeof k["iterations"] !== "number" || typeof k["salt"] !== "string") {
    fail("MalformedRecord", "malformed kdf")
  }

  const slotsRaw = doc["slots"]
  if (!Array.isArray(slotsRaw)) fail("MalformedRecord", "slots is not an array")

  const slots: Slot[] = (slotsRaw as unknown[]).map((entry, i) => {
    if (typeof entry !== "object" || entry === null) return fail("InvalidSlot", `slot ${i} is not an object`)
    const s = entry as Record<string, unknown>
    for (const field of ["id", "type", "label", "hkdfSalt", "wrappedDek", "createdAt"]) {
      if (typeof s[field] !== "string") fail("InvalidSlot", `slot ${i}: missing ${field}`)
    }
    const slot: Slot = {
      id: s["id"] as string,
      type: s["type"] as SlotType,
      label: s["label"] as string,
      hkdfSalt: unb64u(s["hkdfSalt"] as string, `slot ${i} hkdfSalt`),
      wrappedDek: unb64u(s["wrappedDek"] as string, `slot ${i} wrappedDek`),
      createdAt: s["createdAt"] as string,
    }
    if (s["credentialId"] !== undefined) {
      if (typeof s["credentialId"] !== "string") fail("InvalidSlot", `slot ${i}: credentialId`)
      slot.credentialId = unb64u(s["credentialId"], `slot ${i} credentialId`)
    }
    if (s["locatorVaultId"] !== undefined) {
      if (typeof s["locatorVaultId"] !== "string") fail("InvalidSlot", `slot ${i}: locatorVaultId`)
      slot.locatorVaultId = s["locatorVaultId"]
    }
    return slot
  })

  const record: BootstrapRecord = {
    recordVersion: RECORD_VERSION,
    vaultId: doc["vaultId"] as string,
    objectId: doc["objectId"] as string,
    revision: BigInt(doc["revision"]),
    kdf: {
      alg: k["alg"] as KdfParams["alg"],
      iterations: k["iterations"] as number,
      salt: unb64u(k["salt"] as string, "kdf.salt"),
    },
    slots,
  }
  checkRecord(record)

  return { record, mac: unb64u(doc["mac"], "mac") }
}

// --- integrity ---------------------------------------------------------------------------

export async function computeMac(
  record: BootstrapRecord,
  macKey: CryptoKey,
): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.sign("HMAC", macKey, src(canonicalBytes(record))))
}

/**
 * Verified AFTER a successful DEK unwrap, never before — checking earlier would be circular,
 * since the MAC key is derived from the DEK. That ordering is sufficient: pre-unlock tampering
 * with KDF parameters or a slot only causes unwrap to fail, whereas tampering with objectId or
 * revision is a live rollback attack, and that is what this catches.
 */
export async function verifyMac(
  record: BootstrapRecord,
  mac: Uint8Array,
  macKey: CryptoKey,
): Promise<boolean> {
  return crypto.subtle.verify("HMAC", macKey, src(mac), src(canonicalBytes(record)))
}

// --- slots -------------------------------------------------------------------------------

function sameBytes(a: Uint8Array | undefined, b: Uint8Array): boolean {
  if (!a || a.length !== b.length) return false
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false
  return true
}

export function findSlot(
  record: BootstrapRecord,
  by: { credentialId?: Uint8Array; slotId?: string; type?: SlotType },
): Slot | undefined {
  return record.slots.find(
    (slot) =>
      (by.slotId === undefined || slot.id === by.slotId) &&
      (by.type === undefined || slot.type === by.type) &&
      (by.credentialId === undefined || sameBytes(slot.credentialId, by.credentialId)),
  )
}

export function addSlot(record: BootstrapRecord, slot: Slot): BootstrapRecord {
  if (record.slots.some((s) => s.id === slot.id)) {
    fail("DuplicateSlot", `duplicate slot id: ${slot.id}`)
  }
  const next: BootstrapRecord = { ...record, slots: [...record.slots, slot] }
  checkRecord(next)
  return next
}

export function removeSlot(record: BootstrapRecord, slotId: string): BootstrapRecord {
  if (!record.slots.some((s) => s.id === slotId)) fail("SlotNotFound", slotId)
  const next: BootstrapRecord = {
    ...record,
    slots: record.slots.filter((s) => s.id !== slotId),
  }
  checkRecord(next) // rejects removing the last slot
  return next
}

export function withRevision(record: BootstrapRecord, revision: bigint): BootstrapRecord {
  const next: BootstrapRecord = { ...record, revision }
  checkRecord(next)
  return next
}

export function withObjectId(record: BootstrapRecord, objectId: string): BootstrapRecord {
  const next: BootstrapRecord = { ...record, objectId }
  checkRecord(next)
  return next
}
